import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchNewerRelease, type ReleaseManifest } from '../services/releaseUpdateService';

export interface ReleaseUpdateState {
  availableRelease?: ReleaseManifest;
  dismiss: () => void;
  reload: () => void;
}

export function useReleaseUpdate(): ReleaseUpdateState {
  const [availableRelease, setAvailableRelease] = useState<ReleaseManifest>();
  const dismissedVersion = useRef<string>();
  const requestInFlight = useRef(false);
  const mounted = useRef(true);

  const check = useCallback(async () => {
    if (requestInFlight.current) return;
    requestInFlight.current = true;
    try {
      const release = await fetchNewerRelease();
      if (mounted.current && release && release.version !== dismissedVersion.current) setAvailableRelease(release);
    } finally {
      requestInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void check();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      mounted.current = false;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [check]);

  return {
    availableRelease,
    dismiss: () => {
      if (availableRelease) dismissedVersion.current = availableRelease.version;
      setAvailableRelease(undefined);
    },
    reload: () => window.location.reload(),
  };
}
